<script lang="ts">
  import type { TokenMetadata } from '$lib/interfaces';
  import { onMount } from 'svelte';
  import { getCdnImageUrl, type CdnImageOptions } from '$lib/media';

  export let metadata: TokenMetadata;
  export let autoplay = false;
  export let muted = true;
  export let controls = false;
  export let shadow = false;
  export let square = false;
  export let scale = false;
  export let rounded = false;
  export let className = '';

  $: isAnimation = metadata.animationUrl && (autoplay || controls);
  $: extension = metadata.animationUrl?.split('.').pop() || 'mp4';
  $: isModel = extension === 'gltf' || extension === 'glb';

  // Size is based on max rendered size of content on desktop
  let imageSrcOptions: CdnImageOptions;
  $: imageSrcOptions = square
    ? { width: 428, height: 428 }
    : { width: 1920, height: 1080, fit: 'contain' };
  $: imageSrc = getCdnImageUrl(metadata.image, imageSrcOptions);
  $: placeholderUrl = getCdnImageUrl(metadata.image, { ...imageSrcOptions, anim: false });

  onMount(async () => {
    if (isModel) {
      await import('@google/model-viewer');
    }
  });

  function setBackgroundColor(el: HTMLElement, color: string | null | undefined) {
    if (color) {
      el.style.backgroundColor = color;
    }
  }

  function toggleMute(video: HTMLVideoElement, muted: boolean) {
    video.muted = muted;

    return {
      update(muted: boolean) {
        video.muted = muted;
      },
    };
  }
</script>

{#if isModel}
  <model-viewer
    data-chromatic="ignore"
    class:square
    class:rounded
    class:shadow-lg={shadow}
    class:scale
    class="main-token-content {className}"
    loading={autoplay ? 'eager' : 'lazy'}
    {autoplay}
    ar
    camera-controls={controls || undefined}
    poster={placeholderUrl}
    src={metadata.animationUrl}
    use:setBackgroundColor={metadata.backgroundColor}
  />
{:else if isAnimation}
  <video
    data-chromatic="ignore"
    class:square
    class:rounded
    class:shadow-lg={shadow}
    class:scale
    class="main-token-content {className}"
    {controls}
    muted
    {autoplay}
    loop
    playsinline
    poster={placeholderUrl}
    use:toggleMute={muted}
    use:setBackgroundColor={metadata.backgroundColor}
  >
    <source src={metadata.animationUrl} />
    Your browser does not support the video tag.
  </video>
{:else if imageSrc}
  <img
    data-chromatic="ignore"
    class:square
    class:rounded
    class:shadow-lg={shadow}
    class:scale
    class="main-token-content {className}"
    src={imageSrc}
    alt={metadata.name}
    use:setBackgroundColor={metadata.backgroundColor}
  />
{:else}
  <div
    class:square
    class:rounded
    class:shadow-lg={shadow}
    class:scale
    class="placeholder flex items-center justify-center {className}"
  >
    <i class="text-lg text-grey icon-bitski-filled" />
  </div>
{/if}

<style lang="postcss">
  :global(.main-token-content) {
    max-height: 100%;
    max-width: 100%;
    object-fit: scale-down;
  }

  model-viewer,
  .placeholder {
    width: 100%;
    height: 100%;
    min-height: 20rem;
  }

  .square {
    width: 100%;
    height: 100%;
    object-position: 50% 50%;
    min-height: initial;
    object-fit: cover;
    position: absolute;
    top: 0;
  }

  .scale {
    transition: all 200ms ease-out;
  }

  .scale:hover {
    transform: scale(1.05);
  }

  @media (hover: none) {
    .scale:hover {
      transform: none;
    }
  }
</style>
